import { useEffect, useState } from "react";
import { listSessions, getSessionTranscript, type SessionSummaryRow, type SessionTranscript } from "../lib/api";
import { CorrectionBubble } from "./CorrectionBubble";

interface Props { onClose: () => void; teacherName: string; }

function fmtDate(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function durationMin(start: number, end: number | null): string {
  if (!end) return "—";
  const m = Math.round((end - start) / 60000);
  return `${m} min`;
}

export function SessionsView({ onClose, teacherName }: Props): JSX.Element {
  const [list, setList] = useState<SessionSummaryRow[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<SessionTranscript | null>(null);
  const [loadingT, setLoadingT] = useState(false);

  useEffect(() => { void listSessions(40).then(setList); }, []);

  useEffect(() => {
    if (!openId) { setTranscript(null); return; }
    setLoadingT(true);
    void getSessionTranscript(openId).then((t) => {
      setTranscript(t);
      setLoadingT(false);
    });
  }, [openId]);

  if (transcript) {
    return (
      <div className="space-y-3">
        <button onClick={() => { setOpenId(null); setTranscript(null); }} className="text-xs text-ink-400 hover:text-white">← back to list</button>
        <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-4">
          <div className="text-[10px] uppercase tracking-widest text-ink-500">
            {fmtDate(transcript.started_at)} {"·"} {durationMin(transcript.started_at, transcript.ended_at)}
          </div>
          <div className="text-white font-medium mt-1">{transcript.turns.length} messages</div>
        </div>

        <div className="space-y-2">
          {transcript.turns.map((t) => (
            <div key={t.id}>
              <div className={`rounded-xl px-3 py-2 text-sm ${t.role === "user" ? "bg-lime-500/15 border border-lime-500/30 text-white ml-8" : "bg-ink-800/60 border border-ink-700 text-ink-100 mr-8"}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">
                  {t.role === "user" ? "You" : teacherName}
                </div>
                {t.text}
              </div>
              {t.role === "user" && t.analysis && (
                <div className="ml-8 mt-1">
                  <CorrectionBubble userText={t.text} analysis={t.analysis} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (loadingT) {
    return <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">Loading transcript…</div>;
  }
  if (list === null) {
    return <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">Loading sessions…</div>;
  }
  if (list.length === 0) {
    return (
      <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-6 text-center">
        <div className="text-white">No sessions yet.</div>
        <button onClick={onClose} className="mt-3 text-sm text-ink-400 hover:text-white">← Back</button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <button onClick={onClose} className="text-xs text-ink-400 hover:text-white">← back to dashboard</button>
      {list.map((s) => (
        <button
          key={s.id}
          onClick={() => setOpenId(s.id)}
          className="w-full text-left rounded-2xl border border-ink-700 bg-ink-900/60 hover:border-ink-500 p-4 transition"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-ink-500">
              {fmtDate(s.started_at)} {"·"} {durationMin(s.started_at, s.ended_at)}
            </div>
            <div className="text-[10px] text-ink-400 font-mono">
              {s.turn_count} turns {"·"} {s.error_count} errors
            </div>
          </div>
          {s.narrative_summary ? (
            <div className="text-sm text-ink-100 mt-1 line-clamp-2">{s.narrative_summary}</div>
          ) : (
            <div className="text-xs text-ink-500 mt-1 italic">No summary (session may be unfinished).</div>
          )}
          {s.focus_for_next_time && (
            <div className="text-xs text-ink-400 mt-1">Focus: {s.focus_for_next_time}</div>
          )}
        </button>
      ))}
    </div>
  );
}
